import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_THRESHOLD,
  DEFAULT_WEIGHTS,
  WEIGHT_KEYS,
  type MatchWeights,
} from "@/lib/matchScore";

// The tunable matching config, one global row in public.match_config, edited
// by admins in the Matching tab and read here to drive the on-site match %,
// Best-Match sort, and (server-side) the notification threshold.

export interface MatchConfig {
  weights: MatchWeights;
  threshold: number;
}

export const DEFAULT_MATCH_CONFIG: MatchConfig = {
  weights: DEFAULT_WEIGHTS,
  threshold: DEFAULT_THRESHOLD,
};

export const MATCH_CONFIG_QUERY_KEY = ["match_config"] as const;

// Coerce a possibly-partial/legacy jsonb weights blob into a full MatchWeights
// so scoring never produces NaN if a key is missing or malformed.
export const normalizeWeights = (raw: unknown): MatchWeights => {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const out = { ...DEFAULT_WEIGHTS };
  for (const k of WEIGHT_KEYS) {
    const v = obj[k];
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) out[k] = v;
  }
  return out;
};

export function useMatchConfig(enabled = true): MatchConfig {
  const { data } = useQuery({
    queryKey: MATCH_CONFIG_QUERY_KEY,
    enabled,
    queryFn: async (): Promise<MatchConfig> => {
      // match_config isn't in the generated types yet, cast like useSiteAssets.
      const { data, error } = await supabase
        .from("match_config" as never)
        .select("weights, threshold")
        .eq("id", "global")
        .maybeSingle();
      if (error || !data) {
        if (error) console.warn("[useMatchConfig] failed to fetch", error);
        return DEFAULT_MATCH_CONFIG;
      }
      const row = data as unknown as { weights: unknown; threshold: number | null };
      return {
        weights: normalizeWeights(row.weights),
        threshold: typeof row.threshold === "number" ? row.threshold : DEFAULT_THRESHOLD,
      };
    },
    // Config changes rarely; stale-while-revalidate.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  return data ?? DEFAULT_MATCH_CONFIG;
}
